"use client";

import BarChartIcon from "@mui/icons-material/BarChart";
import DeleteIcon from "@mui/icons-material/Delete";
import { Dialog, DialogActions, DialogContent, DialogTitle, Menu } from "@mui/material";
import { useNavigate } from "react-router-dom";

import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";

import { ConfirmationDialog } from "@/components/molecules/ConfirmationDialog";
import { NOTIFICATION_PERMISSIONS } from "@/constants/modules.permissions";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useLanguage from "@/contexts/language-context";
import useRemoveNotification from "@/hooks/requests/notification/use-remove-notification";
import useHasNoPermission from "@/hooks/use-check-permissions";
import useDialog from "@/hooks/use-dialog";
import useMutate from "@/hooks/use-mutate";
import INotification from "@/interfaces/notification.interface";
import { NotificationsMessages } from "@/messages/notifications/notifications.messages";
import NotificationItem from "@/mui/Items/NotificationItem";

interface Props {
  isOpen: boolean;
  anchorEl: (EventTarget & HTMLButtonElement) | null;
  onClose: () => void;
  messages: NotificationsMessages;
  selected: INotification;
}

export function NotificationPopover({ isOpen, anchorEl, onClose, messages, selected }: Props) {
  const { mutate } = useMutate();
  const { remove, isLoading } = useRemoveNotification();
  const { lang } = useLanguage();
  const router = useNavigate();

  const {
    isVisible: isDeleteVisible,
    handleOpen: handleOpenDelete,
    handleClose: handleCloseDelete,
  } = useDialog<INotification>();

  const hasNoPermissionToDelete = useHasNoPermission(NOTIFICATION_PERMISSIONS.DELETE);

  const onSuccess = () => {
    mutate(SWR_KEYS.getNotifications);
    handleCloseDelete();
  };

  const onError = () => handleCloseDelete();

  const onConfirmRemove = async (notification: INotification) => {
    const successMessage = messages.removeNotificationSuccess;
    const errorMessage = messages.removeNotificationError;

    const _id = Number(notification.id);
    await remove({ _id, onSuccess, onError, successMessage, errorMessage });
  };

  return (
    <Menu
      open={isOpen}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <NotificationItem
        icon={<BarChartIcon />}
        disabled={false}
        onClick={() => {
          onClose();
          const url = selected.link || `/notifications/${lang}/${selected.id}`;
          // Remove any base URL and keep only the path
          const path = url.replace(/^(?:https?:\/\/[^/]+)?(.*)$/, "$1");
          router(path);
        }}
        title={messages.details2Popover}
      />

      <NotificationItem
        icon={<DeleteIcon />}
        disabled={hasNoPermissionToDelete}
        onClick={() => handleOpenDelete(selected)}
        title={messages.removePopover}
      />

      <ConfirmationDialog
        isOpen={isDeleteVisible}
        onClose={handleCloseDelete}
        onConfirm={() => onConfirmRemove(selected)}
        titleText={messages.deleteNotificationDialogTitle}
        descriptionText={messages.deleteNotificationDialogContent}
        cancelText={messages.deleteNotificationDialogCancel}
        confirmText={messages.deleteNotificationDialogConfirm}
        isLoading={isLoading}
      />
    </Menu>
  );
}
